/*
 * Keep Smile.io's "Earn N Points when completing this purchase" in step with the
 * bundle offer the customer has selected (BundleX volume discount).
 *
 * Why this exists: products on this store have a single variant, so there is no
 * native variant change for Smile to react to — the amount the customer actually
 * pays comes from the offer widget instead, and Smile keeps showing the points
 * for the base price.
 *
 * Hooks used (taken from BundleX's own bundle: volume-discount.min.js):
 *   .js-bx-offer            each offer card, carries data-offer-key
 *   .bx-active              the selected offer
 *   .js-bx-offer-price      that offer's price
 *   .js-bx-total-price      the running total, when the layout renders one
 *
 * The points-per-currency rate is NOT hardcoded — it is derived on load from what
 * Smile itself rendered (basePoints / unit price), so a change to the earning rule
 * in Smile is picked up automatically. Smile's first render is always for ONE unit
 * of the variant, whatever quantity BundleX has set, so the rate must be taken
 * against the unit price (data-variant-prices, set in layout/theme.liquid) and not
 * the selected offer's price.
 *
 * After that, Smile re-renders on every click with a stale quantity (it reads the
 * quantity before BundleX has updated it) and replaces its text node when it does.
 * That is what made the number look random, so the node is looked up fresh on
 * every write and our value is re-applied after each Smile re-render.
 *
 * Fully defensive: if the Smile block, the offer widget, or a price cannot be
 * read, the script does nothing and the original text is left untouched.
 */
(function () {
  'use strict';

  if (window.__nsSmilePoints) return; // guard against the script being included twice
  window.__nsSmilePoints = true;

  var script = document.currentScript || document.querySelector('script[data-variant-prices]');
  var variantPrices;
  try {
    variantPrices = JSON.parse(script.getAttribute('data-variant-prices'));
  } catch (e) {
    return;
  }
  if (!variantPrices) return;

  var CURRENCY = /[$€£¥]|USD|EUR|GBP/;
  var POINTS_LINE = /(\d[\d.,]*)\s*point/i;

  var smileBlock = document.querySelector('[id*="smile_points_on_product_page"]');
  if (!smileBlock) return;

  var rate = null; // points per unit of currency

  /* ---- price reading ----------------------------------------------------- */

  function isStruck(el) {
    try {
      var s = window.getComputedStyle(el);
      return (s.textDecorationLine || s.textDecoration || '').indexOf('line-through') !== -1;
    } catch (e) {
      return false;
    }
  }

  function amountIn(el) {
    if (!el) return null;
    /* An offer price cell holds both the payable price and the crossed-out
       "was" price, so skip anything struck through. */
    var leaves = el.querySelectorAll('*');
    var nodes = leaves.length ? leaves : [el];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.children.length) continue;
      var t = (n.textContent || '').trim();
      if (!CURRENCY.test(t) || isStruck(n)) continue;
      var m = t.replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
      if (m) return parseFloat(m[1]);
    }
    /* single node with no element children */
    var own = (el.textContent || '').trim();
    if (CURRENCY.test(own) && !isStruck(el)) {
      var m2 = own.replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
      if (m2) return parseFloat(m2[1]);
    }
    return null;
  }

  function selectedPrice() {
    /* the widget's own total wins when the layout renders one */
    var total = amountIn(document.querySelector('.js-bx-total-price'));
    if (total !== null) return total;

    var active = document.querySelector('.js-bx-offer.bx-active');
    if (!active) return null;

    var priced = amountIn(active.querySelector('.js-bx-offer-price'));
    return priced !== null ? priced : amountIn(active);
  }

  /* Price of one unit of the selected variant, from data-variant-prices on the
     script tag. Not derived from the BundleX widget: on load it marks an offer
     active a moment before it sets the form quantity, so anything computed
     from offer price / quantity is wrong for that moment. */
  function unitPrice() {
    var idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return idInput ? variantPrices[idInput.value] || null : null;
  }

  /* ---- points text ------------------------------------------------------- */

  /* Smile swaps its <p> out on every re-render, so the node is never cached. */
  function locatePoints() {
    var els = smileBlock.querySelectorAll('*');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.children.length) continue;
      var t = el.textContent || '';
      var m = t.match(POINTS_LINE);
      if (!m) continue;
      var at = t.indexOf(m[1]);
      return {
        el: el,
        value: parseFloat(m[1].replace(/,/g, '')),
        prefix: t.slice(0, at),
        suffix: t.slice(at + m[1].length)
      };
    }
    return null;
  }

  function render(points) {
    var found = locatePoints();
    if (!found) return;
    var next = found.prefix + points + found.suffix;
    /* only write when it actually changes, so the observer below can't loop */
    if (found.el.textContent !== next) found.el.textContent = next;
  }

  /* Smile floors price x rate, so points / price comes out slightly low
     (49 / 49.99 = 0.98, which would turn 99.98 into 97). Snap to the nearest
     0.05 when that is within rounding error. */
  function toRate(points, price) {
    var raw = points / price;
    var snapped = Math.round(raw * 20) / 20;
    return snapped > 0 && Math.abs(snapped - raw) < 0.05 ? snapped : raw;
  }

  /* ---- wiring ------------------------------------------------------------ */

  function sync() {
    if (rate === null) return;
    var price = selectedPrice();
    if (price === null) return;
    render(Math.floor(price * rate));
  }

  var queued = false;
  function syncSoon() {
    if (queued) return;
    queued = true;
    /* setTimeout rather than requestAnimationFrame: rAF is paused in background
       tabs, which would leave `queued` stuck until the tab is shown again */
    setTimeout(function () {
      queued = false;
      sync();
    }, 0);
  }

  var tries = 0;
  (function calibrate() {
    var found = locatePoints(); // Smile fills the number in after load
    var basePrice = unitPrice(); // BundleX renders its offers after load

    if (found && basePrice && selectedPrice() !== null) {
      rate = toRate(found.value, basePrice);

      document.addEventListener('click', syncSoon, true);
      document.addEventListener('change', syncSoon, true);

      if (window.MutationObserver) {
        /* bx-active moves between offer cards, so watch class changes too */
        new MutationObserver(syncSoon).observe(document.body, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class']
        });
      }

      sync();
      return;
    }

    if (tries++ < 60) setTimeout(calibrate, 250); // ~15s for both apps to render
  })();
})();
